import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import {
  createApiClient,
  currentTenantId,
  forgetApiKey,
} from '../api.js';
import { BookmarkletDragWidget } from '../components/BookmarkletDragWidget.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { COPYABLE_CODE_CSS } from '../components/CopyableCode.jsx';
import { MintKeyModal } from '../components/MintKeyModal.jsx';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  KeyIcon,
} from '../components/icons';
import { useProjectContext } from '../hooks/useProjectContext.js';
import {
  KindBadge,
  OwnershipBadge,
  PurposeBadge,
  formatDate,
} from './ApiKeys.jsx';

/**
 * "My API keys" (card 053) — own-only view.
 *
 *   - Lists only the caller's personal keys (`owner_user_id=me`).
 *     The server-side role-hierarchy filter from card 042 is the
 *     security boundary; this client filter is purely UX-narrowing.
 *   - One primary action: "Mint new key" → modal in personal-only
 *     preset mode (Personal / Bookmarklet / MCP).  `forceOwnership`
 *     pins owner_user_id on the wire so the client cannot mint a
 *     system key via this surface.
 *   - Bookmarklet drag-target inline.  Rows with `purpose=bookmarklet`
 *     (card 048) expand into a draggable javascript: link panel —
 *     replaces the standalone /bookmarklet page.
 *
 * Folds in / replaces:
 *   - `routes/Bookmarklet.jsx` (drag-target lives inline here now).
 *   - `routes/PersonalKeys.jsx` (which had filters for "All
 *     personal" + "System-owned" — those audit views moved to
 *     /keys, the tenant admin surface).
 */
export function MyKeys() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const location = useLocation();
  // Card 064 — if the user was inside a project before they hit
  // /my-keys#bookmarklet (e.g. from a pinned legacy link or a
  // sidebar item that hasn't been updated), surface a banner
  // nudging them at the project-scoped bookmarklet page that
  // actually carries this project's keys.  Card 068 — the cache
  // is per-tenant; pass the active tenant id so the hook only
  // surfaces context from the current tenant.
  const projectCtx = useProjectContext(currentTenantId());

  const [me, setMe] = useState(null); // { user_id, display_name, email }
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMint, setShowMint] = useState(false);
  const [confirmNode, ask] = useConfirm();
  // Default the bookmarklet section to expanded when the user
  // arrived via the /bookmarklet → /my-keys#bookmarklet redirect,
  // so the legacy entry point still feels obvious.
  const arrivedAtBookmarkletAnchor = location.hash === '#bookmarklet';
  const [bookmarkletPanelOpen, setBookmarkletPanelOpen] = useState(
    arrivedAtBookmarkletAnchor,
  );
  const showProjectBanner = (
    arrivedAtBookmarkletAnchor
    && projectCtx
    && !projectCtx.isUrlActive
  );

  // Caller identity.  Without this we can't filter list calls by
  // owner_user_id, so the page waits for /v1/admin/team to land
  // before firing the keys fetch.
  useEffect(() => {
    let alive = true;
    api('/v1/admin/team')
      .then((resp) => {
        if (!alive) return;
        const self = (resp?.members || []).find((m) => m.is_self);
        if (self) {
          setMe({
            user_id: self.platform_user_id,
            display_name: self.display_name || '',
            email: self.email || '',
          });
        } else {
          setError("Couldn't read your tenant identity. Try refreshing.");
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [api]);

  const load = useCallback(async () => {
    if (!me?.user_id) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api(
        `/v1/admin/api-keys?owner_user_id=${encodeURIComponent(me.user_id)}`,
      );
      const all = resp?.keys || [];
      // Own-only — drop archived / revoked.  The /keys admin view
      // surfaces archived rows for cleanup; here we want the
      // "what's live on my behalf" answer.
      setKeys(all.filter((k) => k.active !== false));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, me]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll to the bookmarklet anchor once the panel mounts so
  // an inbound /bookmarklet → /my-keys#bookmarklet redirect lands on
  // the right section instead of the top of the page.
  const bookmarkletAnchorRef = useRef(null);
  useEffect(() => {
    if (!arrivedAtBookmarkletAnchor) return;
    if (loading) return;
    // requestAnimationFrame so the DOM has the section painted
    // before we scroll into view; without this we sometimes scroll
    // a frame too early and miss the now-expanded panel.
    const id = window.requestAnimationFrame(() => {
      bookmarkletAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [arrivedAtBookmarkletAnchor, loading]);

  // Split rows for the two sub-sections.  Bookmarklet purpose keys
  // get the drag panel; everything else lives in a plain list.
  const { bookmarkletKeys, otherKeys } = useMemo(() => {
    const isBookmarklet = (k) =>
      (k.effective_purpose || k.purpose) === 'bookmarklet';
    return {
      bookmarkletKeys: keys.filter(isBookmarklet),
      otherKeys: keys.filter((k) => !isBookmarklet(k)),
    };
  }, [keys]);

  function revokeOne(k) {
    ask({
      title: `Revoke ${k.prefix}…?`,
      body: (
        <>
          <p>
            Any page using this key will get 401 on its next request.
          </p>
          {(k.effective_purpose || k.purpose) === 'bookmarklet' && (
            <p>
              Your bookmark-bar bookmarklet using this key will stop
              working until you replace it with one minted from a
              fresh key.
            </p>
          )}
        </>
      ),
      confirmLabel: 'Revoke',
      danger: true,
      runOnConfirm: async () => {
        await api(
          `/v1/admin/api-keys/${encodeURIComponent(k.prefix)}`,
          { method: 'DELETE' },
        );
        forgetApiKey(k.prefix);
        load();
      },
    });
  }

  return (
    <div className="page">
      <style>{COPYABLE_CODE_CSS}</style>
      <style>{MY_KEYS_CSS}</style>
      <div className="page__title">
        <div style={{ flex: 1 }}>
          <h1>My API keys</h1>
          <p className="page__subtitle">
            Personal keys bound to your user — sk_live_* for MCP /
            CLI / server-side scripts, pk_live_* for the dashboard
            bookmarklet.  Leaving the tenant auto-revokes everything
            here; that's the right blast radius for credentials you
            carry around personally.
          </p>
        </div>
        <div>
          <button
            type="button"
            data-ui-name="mint-key-button"
            className="btn btn--primary"
            onClick={() => setShowMint(true)}
          >
            <AddIcon /> Mint new key
          </button>
        </div>
      </div>

      {showProjectBanner && (
        <div className="my-keys__project-banner" role="status">
          <div>
            <strong>Looking for the bookmarklet for{' '}
              {projectCtx.name}?
            </strong>{' '}
            That lives on the project page — this one is your
            personal-key view.  Project bookmarklets are pk_live_*
            keys tagged for that project.
          </div>
          <Link
            to={`/projects/${projectCtx.slug}/bookmarklet`}
            className="btn btn--primary"
          >
            Open {projectCtx.name}&apos;s bookmarklet
          </Link>
        </div>
      )}

      {loading && (
        <div className="state"><div className="state__title">Loading your keys…</div></div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load keys</div>
          <div>{error}</div>
        </div>
      )}

      {/* ── Bookmarklet section ─────────────────────────────── */}
      {!loading && !error && (
        <section
          className="card"
          id="bookmarklet"
          ref={bookmarkletAnchorRef}
        >
          <header className="my-keys__section-head">
            <div>
              <h2 className="my-keys__section-title">Bookmarklet</h2>
              <p className="my-keys__section-blurb">
                Drag a key's link to your bookmark bar.  Click it on
                any page to boot Spotlight in admin mode — the SDK
                elevates via your tenant JWT at runtime, so the pk
                key in the bookmark URL is safe to leave on a shared
                bookmark bar.
              </p>
            </div>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setBookmarkletPanelOpen((v) => !v)}
              aria-expanded={bookmarkletPanelOpen}
            >
              {bookmarkletPanelOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              {bookmarkletPanelOpen ? 'Collapse' : 'Expand'}
            </button>
          </header>
          {bookmarkletPanelOpen && (
            <BookmarkletPanel
              keys={bookmarkletKeys}
              onMint={() => setShowMint(true)}
              onRevoke={revokeOne}
            />
          )}
        </section>
      )}

      {/* ── Other personal keys ─────────────────────────────── */}
      {!loading && !error && (
        <section className="card">
          <header className="my-keys__section-head">
            <div>
              <h2 className="my-keys__section-title">Other personal keys</h2>
              <p className="my-keys__section-blurb">
                sk_live_* keys for MCP hosts, CLI tools, and scripts
                that act on your tenant as you.  Offboarding revokes
                them automatically.
              </p>
            </div>
          </header>
          {otherKeys.length === 0 ? (
            <div className="state">
              <div className="state__title">No personal keys yet</div>
              <div>
                Use the <strong>Mint new key</strong> button above with
                the <strong>Personal</strong> or <strong>Claude / Codex</strong>{' '}
                preset to mint one.
              </div>
            </div>
          ) : (
            <ul className="list">
              {otherKeys.map((k) => (
                <PersonalKeyRow
                  key={k.prefix}
                  k={k}
                  currentUserId={me?.user_id || ''}
                  onRevoke={() => revokeOne(k)}
                />
              ))}
            </ul>
          )}
        </section>
      )}

      {showMint && (
        <MintKeyModal
          api={api}
          mode="modal"
          presetFilter="personal"
          forceOwnership="personal"
          onCreated={() => { load(); }}
          onClose={() => setShowMint(false)}
        />
      )}

      {confirmNode}
    </div>
  );
}

// ── Bookmarklet drag panel ──────────────────────────────────

function BookmarkletPanel({ keys, onMint, onRevoke }) {
  if (keys.length === 0) {
    return (
      <div className="state" style={{ marginTop: 12, textAlign: 'left' }}>
        <div className="state__title">No bookmarklet keys yet</div>
        <div>
          Hit <strong>Mint new key</strong> and pick the{' '}
          <strong>Bookmarklet</strong> preset.  We&apos;ll stamp{' '}
          <code>purpose=bookmarklet</code> on the row so it shows up
          here ready to drag.
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn btn--primary" onClick={onMint}>
              <KeyIcon /> Mint bookmarklet key
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ul className="list">
      {keys.map((k) => (
        <BookmarkletKeyRow
          key={k.prefix}
          k={k}
          onRevoke={() => onRevoke(k)}
        />
      ))}
    </ul>
  );
}

function BookmarkletKeyRow({ k, onRevoke }) {
  return (
    <li className="list__row bookmarklet-row">
      <div className="list__primary">
        <span className="list__name">
          {k.name || <em style={{ color: 'var(--text-3)' }}>untitled</em>}
          <KindBadge kind={k.kind || 'pk'} />
          <PurposeBadge k={k} />
        </span>
        <span className="list__meta">
          <code>{k.prefix}…</code>
          {' · '}
          {k.project_id ? `project ${k.project_id}` : 'tenant-wide'}
          {' · '}
          created {formatDate(k.created_at)}
          {k.expires_at && <> · expires {formatDate(k.expires_at)}</>}
        </span>
      </div>
      <div className="list__actions">
        <button
          type="button"
          className="btn btn--danger-ghost"
          onClick={onRevoke}
          title="Revoke this key"
        >
          <DeleteIcon /> Revoke
        </button>
      </div>
      <div className="bookmarklet-row__widget">
        {/* Card 063 — shared widget; same shape used on the */}
        {/* project-scoped /projects/:slug/bookmarklet page. */}
        <BookmarkletDragWidget
          apiKey={{ prefix: k.prefix, name: k.name }}
        />
      </div>
    </li>
  );
}

// ── Other personal keys row ─────────────────────────────────

function PersonalKeyRow({ k, currentUserId, onRevoke }) {
  const expired = k.expires_at && new Date(k.expires_at).getTime() < Date.now();
  return (
    <li className="list__row">
      <div className="list__primary">
        <span className="list__name">
          {k.name || <em style={{ color: 'var(--text-3)' }}>untitled</em>}
          <KindBadge kind={k.kind || 'sk'} />
          <OwnershipBadge k={k} currentUserId={currentUserId} />
          <PurposeBadge k={k} />
          {expired && <span className="badge badge--default" style={{ marginLeft: 6 }}>expired</span>}
        </span>
        <span className="list__meta">
          <code>{k.prefix}…</code>
          {' · '}
          {k.project_id ? `project ${k.project_id}` : 'tenant-wide'}
          {' · '}
          created {formatDate(k.created_at)}
          {k.last_used_at && <> · last used {formatDate(k.last_used_at)}</>}
          {k.expires_at && <> · expires {formatDate(k.expires_at)}</>}
        </span>
        {k.scopes && k.scopes.length > 0 && (
          <span className="list__meta" style={{ marginTop: 4 }}>
            scopes:{' '}
            {k.scopes.map((s) => (
              <code key={s} style={{ marginRight: 4, fontSize: 11 }}>{s}</code>
            ))}
          </span>
        )}
      </div>
      <div className="list__actions">
        <button
          type="button"
          className="btn btn--danger-ghost"
          onClick={onRevoke}
        >
          <DeleteIcon /> Revoke
        </button>
      </div>
    </li>
  );
}

// Bookmarklet builder + drag widget live in
// `components/BookmarkletDragWidget.jsx` so the project-scoped
// `/projects/:slug/bookmarklet` page renders the exact same surface
// (card 063).

const MY_KEYS_CSS = `
  .my-keys__section-head {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .my-keys__section-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
  .my-keys__section-blurb {
    margin: 4px 0 0;
    font-size: 12.5px;
    color: var(--text-3);
    line-height: 1.4;
    max-width: 60ch;
  }

  /* Card 064 — banner shown when a user landed at /my-keys#bookmarklet
     while a project context is still live in session.  Nudges them at
     the project-scoped bookmarklet page (card 063) without forcing the
     redirect. */
  .my-keys__project-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: var(--brand-50, #eef2ff);
    border: 1px solid var(--brand-200, #c7d2fe);
    border-radius: var(--r-md, 10px);
    font-size: 13px;
    color: var(--text-1, #0f172a);
    margin-bottom: 12px;
  }
  .my-keys__project-banner > div {
    flex: 1;
    min-width: 0;
  }

  .bookmarklet-row {
    flex-wrap: wrap;
  }
  .bookmarklet-row__widget {
    flex-basis: 100%;
    margin-top: 10px;
  }
`;
